import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';

const PurchaseDetailsModal = ({ purchaseId, onClose }) => {
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await chemicalService.getPurchaseById(purchaseId);
        setPurchase(res.data);
      } catch (err) {
        console.error('Error loading purchase:', err);
        setPurchase(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [purchaseId]);

  if (loading) {
    return (
      <div style={styles.modalOverlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={{ padding: '60px', textAlign: 'center', color: '#7f8c8d' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>
            Loading purchase details...
          </div>
        </div>
      </div>
    );
  }

  if (!purchase) {
    return (
      <div style={styles.modalOverlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={{ padding: '60px', textAlign: 'center', color: '#e74c3c' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠️</div>
            Failed to load details
            <div style={{ marginTop: '20px' }}>
              <button onClick={onClose} style={styles.closeFooterButton}>Close</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const remaining = parseFloat(purchase.grand_total || 0) - parseFloat(purchase.amount_paid || 0);

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>🛒 Purchase Details</h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              {purchase.bill_no} · {purchase.vendor_name}
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={{ padding: '24px 30px' }}>

          {/* ─── Header info ─── */}
          <div style={styles.headerInfo}>
            <div style={styles.headerItem}>
              <span style={styles.headerLabel}>Vendor</span>
              <span style={styles.headerValue}>{purchase.vendor_name}</span>
              {purchase.vendor_phone && (
                <span style={styles.headerSub}>☎ {purchase.vendor_phone}</span>
              )}
            </div>
            <div style={styles.headerItem}>
              <span style={styles.headerLabel}>Bill No.</span>
              <span style={styles.headerValue}>{purchase.bill_no}</span>
            </div>
            <div style={styles.headerItem}>
              <span style={styles.headerLabel}>Date</span>
              <span style={styles.headerValue}>{formatDate(purchase.purchase_date)}</span>
            </div>
            <div style={styles.headerItem}>
              <span style={styles.headerLabel}>Payment Status</span>
              <span style={getStatusBadge(purchase.payment_status)}>
                {formatStatus(purchase.payment_status)}
              </span>
            </div>
          </div>

          {/* ─── Items table ─── */}
          <div style={styles.sectionLabel}>Items Purchased</div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.headerRow}>
                  <th style={styles.th}>Product</th>
                  <th style={styles.th}>Batch No.</th>
                  <th style={styles.th}>Mfg / Expiry</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Qty</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Rate</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {(purchase.items || []).map((item) => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '600' }}>{item.product_name}</div>
                      <div style={{ fontSize: '12px', color: '#95a5a6' }}>{item.product_code}</div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.batchTag}>{item.batch_no}</span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontSize: '12px' }}>Mfg: {formatDate(item.mfg_date)}</div>
                      <div style={{ fontSize: '12px', color: '#95a5a6' }}>
                        Exp: {item.expiry_date ? formatDate(item.expiry_date) : '—'}
                      </div>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '700' }}>
                      {formatQty(item.quantity)} {item.unit}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      Rs {formatNumber(item.rate)}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '700', color: '#27ae60' }}>
                      Rs {formatNumber(item.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ─── Totals ─── */}
          <div style={styles.totalsBox}>
            <div style={styles.totalLine}>
              <span>Subtotal</span>
              <span>Rs {formatNumber(purchase.subtotal)}</span>
            </div>
            {parseFloat(purchase.discount) > 0 && (
              <div style={styles.totalLine}>
                <span>Discount</span>
                <span style={{ color: '#e74c3c' }}>− Rs {formatNumber(purchase.discount)}</span>
              </div>
            )}
            <div style={styles.totalLine}>
              <span>GST ({purchase.gst_percent}%)</span>
              <span>Rs {formatNumber(purchase.gst_amount)}</span>
            </div>
            <div style={{ ...styles.totalLine, ...styles.grandTotalLine }}>
              <span>Grand Total</span>
              <span>Rs {formatNumber(purchase.grand_total)}</span>
            </div>
            <div style={{ ...styles.totalLine, color: '#27ae60' }}>
              <span>Amount Paid</span>
              <span>Rs {formatNumber(purchase.amount_paid)}</span>
            </div>
            {remaining > 0 && (
              <div style={{ ...styles.totalLine, color: '#e74c3c', fontWeight: '700' }}>
                <span>Outstanding</span>
                <span>Rs {formatNumber(remaining)}</span>
              </div>
            )}
          </div>

          {purchase.notes && (
            <div style={styles.notesBox}>
              <strong>Notes:</strong> {purchase.notes}
            </div>
          )}

        </div>

        <div style={styles.modalFooter}>
          <button onClick={onClose} style={styles.closeFooterButton}>Close</button>
        </div>

      </div>
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatNumber = (n) => {
  const num = parseFloat(n) || 0;
  return num.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const formatQty = (n) => {
  const num = parseFloat(n) || 0;
  return num % 1 === 0 ? num.toLocaleString('en-PK') : num.toFixed(2);
};

const formatStatus = (status) => {
  const map = { paid: '✓ Paid', partial: 'Partial', unpaid: 'Unpaid' };
  return map[status] || status;
};

const getStatusBadge = (status) => {
  const palette = {
    paid:    { bg: '#d4edda', fg: '#155724' },
    partial: { bg: '#fff3cd', fg: '#856404' },
    unpaid:  { bg: '#f8d7da', fg: '#721c24' },
  };
  const c = palette[status] || palette.unpaid;
  return {
    background: c.bg, color: c.fg,
    padding: '4px 12px', borderRadius: '12px',
    fontSize: '12px', fontWeight: '600',
    display: 'inline-block', width: 'fit-content',
  };
};

// ─── Styles ───────────────────────────────────────────────

const styles = {
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '12px',
    width: '95%', maxWidth: '950px', maxHeight: '92vh',
    overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '20px 30px', borderBottom: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    position: 'sticky', top: 0, background: 'white', zIndex: 10,
  },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: {
    padding: '8px 16px', background: '#f0f0f0', border: 'none',
    borderRadius: '8px', cursor: 'pointer',
    fontSize: '20px', fontWeight: '700', color: '#666',
  },

  headerInfo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px',
    padding: '16px 18px',
    background: '#f8f9fa',
    borderRadius: '10px',
    marginBottom: '24px',
  },
  headerItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
  headerLabel: {
    fontSize: '11px', color: '#7f8c8d', fontWeight: '600',
    textTransform: 'uppercase', letterSpacing: '0.3px',
  },
  headerValue: { fontSize: '14px', color: '#2c3e50', fontWeight: '700' },
  headerSub: { fontSize: '12px', color: '#95a5a6' },

  sectionLabel: {
    fontSize: '12px', fontWeight: '700',
    color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px',
    marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid #f0f0f0',
  },
  tableWrap: {
    border: '1px solid #e0e0e0', borderRadius: '10px',
    overflow: 'auto', marginBottom: '20px',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  headerRow: { background: '#f8f9fa' },
  th: {
    padding: '12px 14px', textAlign: 'left',
    fontSize: '12px', fontWeight: '700',
    color: '#7f8c8d', textTransform: 'uppercase',
    letterSpacing: '0.3px', whiteSpace: 'nowrap',
  },
  td: { padding: '12px 14px', fontSize: '14px', color: '#2c3e50' },
  batchTag: {
    background: '#e3f2fd', color: '#1565c0',
    padding: '4px 10px', borderRadius: '10px',
    fontSize: '12px', fontWeight: '600',
    fontFamily: 'monospace',
  },

  totalsBox: {
    background: '#f8f9fa',
    borderRadius: '10px',
    padding: '16px 20px',
    display: 'flex', flexDirection: 'column', gap: '4px',
    marginBottom: '16px',
  },
  totalLine: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 0',
    fontSize: '14px',
    color: '#2c3e50',
  },
  grandTotalLine: {
    marginTop: '6px',
    paddingTop: '12px',
    borderTop: '2px solid #2c3e50',
    fontSize: '18px',
    fontWeight: '700',
  },
  notesBox: {
    background: '#fffbea', padding: '12px 14px',
    borderRadius: '8px', fontSize: '13px',
    color: '#856404', borderLeft: '4px solid #f39c12',
  },

  modalFooter: {
    padding: '16px 30px',
    borderTop: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'flex-end',
    position: 'sticky', bottom: 0, background: 'white',
  },
  closeFooterButton: {
    padding: '12px 24px',
    background: '#95a5a6', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
};

export default PurchaseDetailsModal;